import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';

interface Props {
  text: string;
}

const MESSAGE_CHANGE_INTERVAL_MS = 6000;

const STATUS_MESSAGES: Record<string, string[]> = {
  collecting_params: [
    'ご要望を整理しています…',
    '検索に必要な条件を確認しています…',
    '日付や行き先を読み取っています…'
  ],
  transport_searching: [
    'ご指定の条件を確認しています…',
    '出発地・到着地の情報を照合しています…',
    '利用可能な交通手段を照会しています…',
    '該当する便の空席状況を確認しています…',
    '所要時間を算出しています…',
    '料金条件を確認しています…',
    '移動時間と費用のバランスを比較しています…',
    '業務スケジュールに適した候補を抽出しています…',
    '最適な順に並べ替えています…',
    '候補を絞り込んでいます…',
    '表示内容を最終確認しています…'
  ],
  hotel_searching: [
    'ご指定の条件を確認しています…',
    '利用可能なホテルを照会しています…',
    '料金とプランを比較しています…'
  ],
  recommending: [
    'おすすめの候補を選んでいます…',
    'ご提案内容をまとめています…',
    '候補を比較して絞り込んでいます…'
  ],
  calculating_slots: [
    '移動時間と予定の空きを照合しています…',
    '候補日の空き時間を算出しています…',
    '予定の重なりを確認しています…'
  ],
  estimating_price: ['費用の相場を確認しています…', '料金の目安を算出しています…'],
  calendar_referencing: [
    'カレンダーの予定を確認しています…',
    'スケジュールの空き状況を照合しています…',
    '期間内の予定件数を集計しています…',
    '候補日の空き時間を算出しています…',
    '予定の重なりを確認しています…'
  ],
  help_searching: [
    '使い方のご案内を探しています…',
    '関連するヘルプ情報を照会しています…',
    '操作方法をまとめています…'
  ]
};

const STEPS: { label: string }[] = [{ label: '条件整理' }, { label: '情報収集' }, { label: '提案' }];

const STEP_INDEX_BY_KEY: Record<string, number> = {
  collecting_params: 0,
  transport_searching: 1,
  hotel_searching: 1,
  calendar_referencing: 1,
  calculating_slots: 1,
  estimating_price: 1,
  help_searching: 1,
  recommending: 2
};

const resolveMessage = (key: string, usedIndices: Set<number>): { message: string; index: number } => {
  const messages = STATUS_MESSAGES[key];
  if (!messages || messages.length === 0) {
    return { message: key, index: -1 };
  }

  const availableIndices = messages.map((_, i) => i).filter(i => !usedIndices.has(i));

  const candidates = availableIndices.length > 0 ? availableIndices : messages.map((_, i) => i);
  const randomIndex = candidates[Math.floor(Math.random() * candidates.length)];

  return { message: messages[randomIndex], index: randomIndex };
};

const OptimisticMessage = ({ text }: Props) => {
  const [currentMessage, setCurrentMessage] = useState('');
  const usedIndicesRef = useRef<Set<number>>(new Set());
  const isFirstRender = useRef(true);

  const styles = useMemo(() => getStyles(), []);

  // 到達済みの最大ステップを単調増加で保持する（後退して見せない）。effect+setState による
  // カスケード再描画を避けるため、レンダー中に ref で派生させる。
  const currentStep = STEP_INDEX_BY_KEY[text] ?? -1;
  const reachedStepRef = useRef(-1);
  if (currentStep > reachedStepRef.current) {
    reachedStepRef.current = currentStep;
  }
  const reachedStep = reachedStepRef.current;

  const selectNewMessage = useCallback(() => {
    const { message, index } = resolveMessage(text, usedIndicesRef.current);
    if (index >= 0) {
      usedIndicesRef.current.add(index);
      const messages = STATUS_MESSAGES[text];
      if (messages && usedIndicesRef.current.size >= messages.length) {
        usedIndicesRef.current.clear();
        usedIndicesRef.current.add(index);
      }
    }
    return message;
  }, [text]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setCurrentMessage(selectNewMessage());
    }
  }, [selectNewMessage]);

  useEffect(() => {
    if (!currentMessage) return undefined;

    const timer = setInterval(() => {
      setCurrentMessage(selectNewMessage());
    }, MESSAGE_CHANGE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [currentMessage, selectNewMessage]);

  if (!currentMessage) return null;

  return (
    <>
      <style>{keyframesStyle}</style>
      <div style={styles.container}>
        {reachedStep >= 0 && <StepIndicator reachedStep={reachedStep} currentStep={currentStep} />}
        <span style={styles.shimmerText}>{currentMessage}</span>
      </div>
    </>
  );
};

const StepIndicator = ({ reachedStep, currentStep }: { reachedStep: number; currentStep: number }) => {
  const styles = stepStyles;
  return (
    <div style={styles.row}>
      {STEPS.map((step, i) => {
        const reached = i <= reachedStep;
        const isCurrent = i === currentStep;
        return (
          <React.Fragment key={step.label}>
            {i > 0 && <span style={{ ...styles.connector, ...(reached ? styles.connectorReached : {}) }} />}
            <span style={styles.stepCell}>
              <span
                style={{
                  ...styles.dot,
                  ...(reached ? styles.dotReached : {}),
                  ...(isCurrent ? styles.dotCurrent : {})
                }}
              />
              <span style={{ ...styles.label, ...(reached ? styles.labelReached : {}) }}>{step.label}</span>
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const keyframesStyle = `
  @keyframes optimistic-message-shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }
`;

const getStyles = (): { container: CSSProperties; shimmerText: CSSProperties } => ({
  container: {
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '10px'
  },
  shimmerText: {
    color: '#666666',
    background:
      'linear-gradient(90deg, #666666 0%, #666666 35%, #aaaaaa 45%, #cccccc 50%, #aaaaaa 55%, #666666 65%, #666666 100%)',
    backgroundSize: '200% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'optimistic-message-shimmer 2s infinite linear'
  }
});

const REACHED_COLOR = '#2f80ed';
const PENDING_COLOR = '#cfcfcf';

const stepStyles: Record<string, CSSProperties> = {
  row: {
    display: 'flex',
    alignItems: 'center'
  },
  stepCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  },
  connector: {
    width: '20px',
    height: '2px',
    backgroundColor: PENDING_COLOR,
    margin: '0 6px'
  },
  connectorReached: {
    backgroundColor: REACHED_COLOR
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: PENDING_COLOR,
    flex: '0 0 auto'
  },
  dotReached: {
    backgroundColor: REACHED_COLOR
  },
  dotCurrent: {
    boxShadow: '0 0 0 3px rgba(47, 128, 237, 0.25)'
  },
  label: {
    fontSize: '11px',
    color: PENDING_COLOR
  },
  labelReached: {
    color: REACHED_COLOR
  }
};

export default OptimisticMessage;
